import styles from "./day-schedule.module.css";

import { useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Flex, Tag, Tooltip, Popover } from "antd";

import routes from "@/routes";

import TaskCard from "../cards/TaskCard";

import { STATUS_OPTIONS } from "@/constants/tasks";

import { formatMilliseconds } from "@/utils";

const getIcon = (color) => {
  return STATUS_OPTIONS.find((item) => item.value === color)?.icon;
};

const hours = Array.from({ length: 24 }, (_, i) => i);

const DaySchedule = ({
  // возвращает список задач разложенных по часам
  data,
  style,
  day,
  nearest,
  showEmpty = false,
  statusPlacement = "front",
  scrollContainerRef,
  statuses,
  priorities,
  taskTypes,
}) => {
  const tasksByHour = useMemo(() => {
    const result = Array.from({ length: 24 }, () => []);
    data.forEach((task) => {
      if (!task.due_date && !task.scheduled_due_date) return;

      const dueHour = new Date(
        task.due_date || task.scheduled_due_date
      ).getHours();
      result[dueHour].push(task);
    });
    return result;
  }, [data]);

  const firstHourWithTasks = useMemo(() => {
    for (let h = 0; h < 24; h++) {
      if (tasksByHour[h]?.length) return h;
    }
    return null;
  }, [tasksByHour]);

  // получаем текущий час
  const currentHour = useMemo(() => new Date().getHours(), []);

  const isToday = useMemo(() => day.getDate() === new Date().getDate(), [day]);

  const internalScrollRef = useRef(null);
  const firstRowRef = useRef(null);

  useEffect(() => {
    const container = scrollContainerRef?.current || internalScrollRef.current;
    const target = firstRowRef.current;
    if (!container || !target) return;

    const extraOffset = nearest?.event ? 30 : 0; // отступ при наличии уведомления
    const containerTop = container.getBoundingClientRect().top;
    const targetTop = target.getBoundingClientRect().top;
    const delta = targetTop - containerTop - extraOffset;

    container.scrollTop += delta;
  }, [firstHourWithTasks, scrollContainerRef, nearest]);

  const content = (
    <Flex vertical gap={2}>
      {hours.map((hour) => (
        <Flex key={hour}>
          <div
            ref={firstHourWithTasks === hour ? firstRowRef : null}
            className={`${styles.hour}${
              isToday && currentHour === hour ? ` ${styles.currentHour}` : ""
            }`}
          >
            {hour}:00
          </div>

          <div className={styles.task_container}>
            {tasksByHour[hour].map((task) => {
              const taskContent = (
                <Flex
                  key={task.uuid}
                  gap={10}
                  style={{ padding: "2px 8px", whiteSpace: "nowrap" }}
                  align="center"
                >
                  {statusPlacement === "front" && (
                    <div className={styles.task_status}>
                      <Tooltip
                        title={
                          !task.scheduled_due_date
                            ? task.status?.name
                            : "Планируется"
                        }
                      >
                        <Tag
                          color={
                            !task.scheduled_due_date ? task.status?.color : ""
                          }
                          icon={getIcon(
                            !task.scheduled_due_date
                              ? task.status?.color
                              : "gray"
                          )}
                          className="tag-with-icon-color small no-margin"
                        />
                      </Tooltip>
                    </div>
                  )}

                  <div className={styles.task_number}>
                    {task.task_number || task?.template?.template_number}
                    {(task.parent || task.template?.parent) &&
                      ` / ${
                        task.parent.task_number ||
                        task.template?.parent?.task_number
                      }`}
                  </div>

                  <div className={styles.task_name}>
                    {statusPlacement === "front" ? (
                      task.scheduled_due_date ? (
                        <div>{task.name || task.template?.name}</div>
                      ) : (
                        <Link to={routes.taskDetail(task.uuid)}>
                          {task.name || task.template?.name}
                        </Link>
                      )
                    ) : (
                      <Tooltip title={task.name || task.template?.name}>
                        {task.scheduled_due_date ? (
                          <div>{task.name || task.template?.name}</div>
                        ) : (
                          <Link to={routes.taskDetail(task.uuid)}>
                            {task.name || task.template?.name}
                          </Link>
                        )}
                      </Tooltip>
                    )}
                  </div>

                  {statusPlacement === "back" && (
                    <div className={styles.task_status}>
                      <Tooltip
                        title={
                          !task.scheduled_due_date
                            ? task.status?.name
                            : "Планируется"
                        }
                      >
                        <Tag
                          color={
                            !task.scheduled_due_date ? task.status?.color : ""
                          }
                          icon={getIcon(
                            !task.scheduled_due_date
                              ? task.status?.color
                              : "gray"
                          )}
                          className="tag-with-icon-color small no-margin"
                        />
                      </Tooltip>
                    </div>
                  )}
                </Flex>
              );

              return statusPlacement === "front" ? (
                <Popover
                  arrow={false}
                  placement="bottomLeft"
                  key={task.uuid}
                  content={
                    <div
                      style={{
                        width: 370,
                        margin: "-12px",
                      }}
                    >
                      <TaskCard
                        task={task}
                        showActions={false}
                        statuses={statuses}
                        priorities={priorities}
                        taskTypes={taskTypes}
                      />
                    </div>
                  }
                >
                  {taskContent}
                </Popover>
              ) : (
                taskContent
              );
            })}
          </div>
        </Flex>
      ))}
    </Flex>
  );

  return (
    <Flex vertical gap={8} style={style} className={styles.day_schedule}>
      {nearest?.event && (
        <Link
          key={nearest.event.uuid}
          className={styles.nearest}
          to={routes.taskDetail(nearest.event.uuid)}
        >
          <span className={styles.nearest_time}>
            Осталось {formatMilliseconds(nearest.timeLeftMs)} до
          </span>{" "}
          <span className={styles.nearest_task_number}>
            {nearest.event.task_number}
          </span>{" "}
          <span className={styles.nearest_event_name}>
            {nearest.event.name}
          </span>
        </Link>
      )}

      {!!data.length || !showEmpty ? (
        scrollContainerRef?.current ? (
          content
        ) : (
          <div>{content}</div>
        )
      ) : (
        <div className={styles.empty}>
          {day.toLocaleDateString()} нет событий
        </div>
      )}
    </Flex>
  );
};

export default DaySchedule;
